<template>
  <div class="flex items-start w-full">
    <div v-if="_value.service" class="hidden md:block self-stretch relative pr-3">
      <div
        class="w-1 rounded h-full"
        :style="{
          backgroundColor: '#' + _value.service.borderHexColour
        }"
      />
    </div>
    <div class="w-full">
      <div class="w-full mb-px">
        <p class="font-semibold text-sm text-gray-700 dark:text-gray-200">
          {{ _value.service.title }}
        </p>
      </div>
      <div class="w-full flex space-x-3">
        <o-field-group
          class="w-full md:w-1/2"
          :label="$t('general.employee')"
          label-class="hidden text-sm text-gray-600 dark:text-gray-300"
        >
          <template #control>
            <o-tooltip v-if="_service.employee && conflictIDs.includes(_service.employee.idEmployee)" :text="$t('components.reservation.availability.manual.controller.conflict')" class="inline-block">
              <o-icon icon="warningBold" fill class="text-yellow-600 mx-1 dark:text-yellow-400" />
            </o-tooltip>
          </template>

          <employee-select
            v-model="_service.employee"
            :types="['employee']"
            class="w-full"
            :filter="employeeFilter"
          />
        </o-field-group>

        <o-field-group class="w-full md:w-1/2" :label="$t('general.startTime')" label-class="hidden text-sm text-gray-600 dark:text-gray-300">
          <time-field v-model="_value.startTime" mode="time" :augmenter="Augmenters.minutes" @change="handleChange" />
        </o-field-group>
      </div>
      <div v-if="hasResources" class="w-full flex flex-wrap mt-4 -mx-2">
        <template v-for="(resource, resourceIndex) in _value.resources" :key="resourceIndex">
          <o-field-group

            :label="resource.serviceResource.name"
            class="w-full md:w-1/2 px-2 mb-3"
          >
            <template #control>
              <o-tooltip v-if="resource.resource && conflictIDs.includes(resource.resource.idEmployee)" :text="$t('components.reservation.availability.manual.controller.conflict')" class="inline-block">
                <o-icon icon="warningBold" fill class="text-yellow-600 mx-1 dark:text-yellow-400" />
              </o-tooltip>
            </template>

            <resource-select
              v-model="_service.resources[resourceIndex].resource"
              class="w-full bg-white shadow-sm rounded dark:bg-gray-900"
              :service="_service.service"
              :resource="resource"
              allow-none
              :availability-options="resourceSelectAvailabilityOptions(resource)"
            />
          </o-field-group>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import EmployeeSelect from '@/components/employee/elements/Select.vue'
import ResourceSelect from '@/components/resource/elements/Select.vue'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import {
  TimeField,
} from '@/components/fields'

import { useEmployeeStore } from '~/stores/useEmployeeStore'

interface Props {
  modelValue: Record<string, any>
  service: Record<string, any>
  date: Date
  conflicts?: any[]
  move?: Record<string, any> | null
}

interface Emits {
  (e: 'update:modelValue', value: any): void
  (e: 'update:service', value: any): void
  (e: 'change'): void
}

const props = withDefaults(defineProps<Props>(), {
  conflicts: () => [],
  move: null,
})

const emit = defineEmits<Emits>()

const employeeStore = useEmployeeStore()
const { $date } = useNuxtApp()

const _value = computed({
  get () {
    return props.modelValue
  },
  set (value) {
    emit('update:modelValue', value)
  },
})

const _service = computed({
  get () {
    return props.service
  },
  set (service) {
    emit('update:service', service)
  },
})

const hasResources = computed(() => {
  return props.service && props.service.resources && props.service.resources.length
})

const conflictIDs = computed(() => {
  const service = props.conflicts?.find((s: any) => s.service.idService === _value.value.service.idService)

  return service?.conflicts?.reduce((acc: any[], conflict: any) => {
    acc.push(conflict.employee.idEmployee)
    return acc
  }, []) || []
})

watch(() => _value.value.employee, (employee) => {
  if (!employee) {
    const value = employeeStore.employeesForService(props.service.service)
    _service.value.employee = value[0] || null
  }
}, { immediate: true })

function employeeFilter (employee: any) {
  const service = props.service.service
  return service && service.employeeIDs ? service.employeeIDs.includes(employee.idEmployee) : false
}

function handleChange () {
  emit('change')
}

function resourceSelectAvailabilityOptions (resource: any) {
  return {
    service: _service.value.service,
    date: $date.format(props.date),
    resource,
    reservations: props.move ? [props.move] : [],
    startTime: _value.value.startTime,
  }
}
</script>
